/**
 * La landing.
 *
 * LAS CIFRAS NO ESTÁN ESCRITAS A MANO. Salen de `cifras.ts`, que genera
 * `scripts/generar-cifras.ts` contando el catálogo, y `verificar:cifras` las
 * comprueba en cada build.
 *
 * No es elegancia: una landing con cifras a mano se desincroniza del producto
 * en la primera semana, y aquí una cifra desincronizada es una afirmación
 * falsa sobre un producto de salud. Ya pasó una vez —decía 247 después de
 * quitar la familia de oído— y por eso ahora lo impide el build.
 *
 * Y no se calculan en el navegador: importar el catálogo desde aquí metería
 * las ciento treinta y ocho fichas, con toda su prosa y en tres idiomas, en el
 * bundle de la página que alguien abre desde un anuncio con mala cobertura.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 15/09/2026 · LA PÁGINA SE REDIBUJA ENTERA, POR EL REPASO DEL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hasta hoy cada bloque se pintaba igual —etiqueta, titular, párrafo y una
 * lista— en una columna de 46rem pegada al centro, y el dueño lo marcó sobre
 * las capturas: «revisa la simetría de la landing, que no tiene equilibrio ni
 * belleza», «todo acá grita incompetencia, UX/UI de mierda» (el precio), «esta
 * primario de feo» (las dudas), «mejora mucho el footer». Y en la cabecera:
 * el isotipo («se parece a una vagina»), las secciones de la página al centro
 * («categorías de la landing») y los tres idiomas «en un menú desplegable
 * bonito».
 *
 * Ahora cada bloque tiene su forma, según lo que cuenta (su porqué, en
 * `copy.ts`): cabeceras de sección centradas, rejillas simétricas, el precio
 * en una tarjeta, las preguntas en acordeón, y un pie de cuatro columnas sin la
 * dirección postal. La página de antes está en git (commit `ae73324`).
 */

import { useEffect, useRef, useState } from "react";

import {
  CORREO_DE_CONTACTO,
  ENLACE_DE_CORREO,
  ENLACE_DE_WHATSAPP,
  ETIQUETA_BCP47,
  HORARIO,
  IDIOMA_POR_DEFECTO,
  IDIOMAS,
  caminoDelLegal,
  NOMBRE_DEL_LEGAL,
  negociarIdioma,
  NOMBRE_DEL_IDIOMA,
  TELEFONO_LEIDO,
  type Idioma,
  type TipoLegal,
} from "@vc/contenido";
import {
  CAMINO_DE_EMPEZAR,
  /* 13/09: los botones «Empezar» abren el onboarding, sin pasar por la portada
     de la plataforma (recorrido como clienta nueva: eran dos «Empezar» seguidos). */
  CAMINO_DE_EMPEZAR_DESDE_LA_VENTA,
  CAMINO_DE_ENTRAR,
  CAMINO_DE_PAGAR,
} from "../../apps/api/src/http/caminos.ts";
import { traducir, type Clave } from "@vc/i18n";
import { Isotipo } from "@vc/ui/marca";
import { Modal } from "@vc/ui";
import { COPY, elCobroEnPesos, PRECIO, type Bloque } from "./copy.ts";
import {
  IconoAltavoz,
  IconoAuriculares,
  IconoBocadillo,
  IconoBrujula,
  IconoCerrar,
  IconoCheck,
  IconoCorazon,
  IconoEscudo,
  IconoFlecha,
  IconoGlobo,
  IconoGrafica,
  IconoHoja,
  IconoLibro,
  IconoLista,
  IconoLuna,
  IconoMaletin,
  IconoMas,
  IconoMegafono,
  IconoMenu,
  IconoMicrofono,
  IconoNota,
  IconoOnda,
  IconoPausa,
  IconoPortapapeles,
  IconoReloj,
  IconoSello,
  IconoTexto,
} from "./iconos.tsx";

/**
 * Las secciones que salen en el menú de la cabecera y en el pie.
 *
 * 15/09: el dueño pidió «categorías de la landing» en el centro de la barra.
 * No son todas: son las cinco a las que alguien quiere saltar sin leer lo de
 * en medio. Los ids son los de `copy.ts`, que son los mismos en los tres
 * idiomas (`verificar:copy` lo exige).
 */
const SECCIONES_DEL_MENU: readonly { readonly id: string; readonly clave: Clave }[] = [
  { id: "como-funciona", clave: "venta.nav_como_funciona" },
  { id: "para-quien", clave: "venta.nav_para_quien" },
  { id: "asistente", clave: "venta.nav_asistente" },
  { id: "precio", clave: "venta.nav_precio" },
  { id: "preguntas", clave: "venta.nav_preguntas" },
];

/** Dónde se recuerda el idioma. Una sola clave, y con el prefijo de la casa. */
const CLAVE_DEL_IDIOMA = "vc.idioma";

function leerIdiomaGuardado(): Idioma | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const v = localStorage.getItem(CLAVE_DEL_IDIOMA);
    return v !== null && (IDIOMAS as readonly string[]).includes(v) ? (v as Idioma) : null;
  } catch {
    // Navegación privada, o el navegador con el almacenamiento bloqueado. No
    // es un fallo: se cae al idioma del navegador, que es el que había antes.
    return null;
  }
}

function guardarIdioma(i: Idioma): void {
  try {
    localStorage.setItem(CLAVE_DEL_IDIOMA, i);
  } catch {
    // Igual: que no se pueda recordar no puede romper la página.
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL IDIOMA VA EN LA URL, Y ES UN PROBLEMA DE VENTA, NO DE TIPOGRAFÍA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La página se vende en tres idiomas y tenía UNA SOLA dirección. O sea:
 *
 *   · Quien la leía en portugués y se la mandaba a un colega por WhatsApp le
 *     mandaba una página que al otro le salía en inglés. Un producto que se
 *     vende en tres mercados se compartía en uno.
 *   · Y un buscador indexaba una sola versión: Googlebot no manda
 *     `Accept-Language`, así que las otras dos no existían para nadie que
 *     buscara «ejercicios para la voz» en castellano o en portugués.
 *
 * Con `?idioma=`, la dirección que hay en la barra es la que se comparte, y el
 * servidor sirve la cabecera correcta a quien la abra — incluida la vista
 * previa del enlace, que no ejecuta JavaScript.
 *
 * `replaceState` y no `pushState`: cambiar de idioma no es navegar, y llenar el
 * historial de idiomas hace que «atrás» deje de significar «atrás».
 */
function leerIdiomaDeLaUrl(): Idioma | null {
  if (typeof location === "undefined") return null;
  const v = new URLSearchParams(location.search).get("idioma");
  return v !== null && (IDIOMAS as readonly string[]).includes(v) ? (v as Idioma) : null;
}

function ponerIdiomaEnLaUrl(i: Idioma): void {
  if (typeof history === "undefined" || typeof location === "undefined") return;
  const u = new URL(location.href);
  u.searchParams.set("idioma", i);
  history.replaceState(null, "", `${u.pathname}${u.search}${u.hash}`);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL BOTÓN DE PAGAR DE LA PÁGINA DE VENTA (D31)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hasta el 05/09 no había ninguno en todo el producto. El dueño lo pidió así:
 * «si la persona aprieta en pagar, que salga un error que diga tipo,
 * suscripciones desactivadas en ese momento».
 *
 * 15/09: vive dentro de la tarjeta del precio, debajo de «Crear mi plan». El
 * orden de la compra es plan → cuenta → pago, y el primer botón es el del
 * primer paso.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LOS TRES FINALES, Y EL 401 ES EL QUE IMPORTA EL DÍA DE LA PASARELA
 *
 *   · 503 con `suscripciones_desactivadas` → lo de hoy. Se dice, y se dice
 *     también qué SÍ se puede hacer, que es montar el plan.
 *   · 401 → el día que haya pasarela. Aquí no hay sesión: quien lee esta
 *     página no ha entrado. No se le enseña una negativa, se le lleva a la
 *     plataforma, que es donde se hace la cuenta. Un cobro sin persona detrás
 *     no se puede reconciliar cuando vuelva el webhook.
 *   · 200 con `ir` → a la pasarela.
 *
 * Y CUALQUIER OTRO FALLO NO SE LLAMA «DESACTIVADAS». Un 503 lo devuelve también
 * un proxy delante mientras el servidor se reinicia: tratar todo 503 como
 * suscripciones apagadas le diría a quien pasa por aquí, durante un despliegue
 * de dos minutos, que este producto ya no se vende.
 */
function BotonDePagar({ idioma }: { readonly idioma: Idioma }): React.ReactElement {
  const [nota, setNota] = useState<string | null>(null);
  /*
    EL MODAL, IGUAL QUE EN LA PLATAFORMA (07/09/2026).

    Palabras del dueño: *«si clica en ir a la pasarela, sale modal diciendo que
    las suscripciones estan cerradas por el momento»*. La caja es la misma de
    `@vc/ui` que usa la plataforma. Escribir aquí otra sería tener dos modales
    que un día se cierran de dos maneras.
  */
  const [cerradas, setCerradas] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /* Adónde vuelve el foco al cerrar. Este botón se deshabilita mientras pide,
     y un botón deshabilitado pierde el foco: sin la referencia, quien navega
     con teclado sale del modal al principio de la página. */
  const elBoton = useRef<HTMLButtonElement>(null);

  async function pulsar(): Promise<void> {
    setOcupado(true);
    try {
      const r = await fetch(CAMINO_DE_PAGAR, { method: "POST" });
      if (r.status === 401) {
        location.assign(CAMINO_DE_EMPEZAR);
        return;
      }

      const d = (await r.json().catch(() => null)) as
        | { readonly ir?: unknown; readonly error?: { readonly codigo?: unknown } }
        | null;

      if (!r.ok) {
        if (d?.error?.codigo === "suscripciones_desactivadas") {
          setCerradas(true);
          return;
        }
        /* La misma frase genérica que usa la plataforma: «algo no ha salido,
           prueba en un momento». */
        setNota(traducir(idioma, "micuenta.algo_fue_mal"));
        return;
      }
      if (typeof d?.ir !== "string" || d.ir === "") {
        setNota(traducir(idioma, "micuenta.algo_fue_mal"));
        return;
      }
      location.assign(d.ir);
    } catch {
      setNota(traducir(idioma, "micuenta.algo_fue_mal"));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <button
        className="vc-boton vc-boton--callado la-boton-ancho"
        type="button"
        ref={elBoton}
        disabled={ocupado}
        onClick={() => { void pulsar(); }}
      >
        {traducir(idioma, "pagar.boton")}
      </button>
      {/* `role="status"` y no `alert`: es una respuesta a algo que la persona
          acaba de pulsar, no una alarma. */}
      {nota === null ? null : (
        <p className="la-precio__apagada" role="status">{nota}</p>
      )}
      {cerradas ? (
        <Modal
          titulo={traducir(idioma, "pagar.cerradas_titulo")}
          etiquetaDeCerrar={traducir(idioma, "app.cerrar")}
          volverElFocoA={elBoton}
          onCerrar={() => { setCerradas(false); }}
        >
          <p>{traducir(idioma, "pagar.desactivadas")}</p>
          <p>{traducir(idioma, "pagar.desactivadas_en_la_venta")}</p>
          <p>
            <a className="vc-boton" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>
              {traducir(idioma, "app.empezar")}
            </a>
          </p>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * La hora, escrita como se escribe en cada idioma.
 *
 * En inglés de Estados Unidos se lee «8 am» / «6 pm»; en castellano y en
 * portugués, el reloj de veinticuatro. Los números salen de `HORARIO`, no de la
 * frase: escritos a mano en tres idiomas, el día que el dueño cambie el horario
 * cambia en uno.
 */
function laHora(n: number, idioma: Idioma): string {
  if (idioma !== "en") return `${String(n)}:00`;
  const sufijo = n < 12 ? "am" : "pm";
  const doce = n % 12 === 0 ? 12 : n % 12;
  return `${String(doce)} ${sufijo}`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL BOTÓN FLOTANTE DE WHATSAPP (D37)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · `<a>` y no `<button>`: es navegación, no una acción.
 *   · SIN la clase `vc-boton`, y no es descuido: `verificar:embudo` exige que
 *     todo `vc-boton` lleve a la plataforma o a un ancla de esta página, y
 *     éste lleva fuera a propósito.
 *   · `rel="noopener noreferrer"`: no tiene por qué llegarle a WhatsApp desde
 *     qué página exacta venía alguien que estaba leyendo sobre su voz.
 *   · El icono es un SVG en línea: la CSP de esta casa es `default-src 'self'`.
 */
function WhatsAppFlotante({ idioma }: { readonly idioma: Idioma }): React.ReactElement {
  const etiqueta = traducir(idioma, "contacto.whatsapp_flotante");
  return (
    <a
      className="la-wasap"
      href={ENLACE_DE_WHATSAPP}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={etiqueta}
      title={etiqueta}
    >
      <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.41a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.15.16-.29.18-.53.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.47c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.06s.89 2.39 1.01 2.56c.12.16 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.15-1.18-.06-.11-.22-.17-.47-.29Z"
        />
      </svg>
    </a>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL SELECTOR DE IDIOMA · un desplegable (15/09/2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eran tres botones en fila —«Español», «English (US)», «Português (BR)»— y el
 * dueño los marcó: «junta en un menú desplegable bonito». Un botón con el globo
 * y el código del idioma abre la lista; se cierra al elegir, con Escape o
 * pulsando fuera.
 *
 * Los 44 px de dedo se mantienen, que es lo que se ganó con los botones viejos
 * (el porqué, en `landing.css`): el botón y cada opción los tienen.
 */
function SelectorDeIdioma({
  idioma,
  alCambiar,
  arriba = false,
}: {
  readonly idioma: Idioma;
  readonly alCambiar: (i: Idioma) => void;
  /** En el pie la lista se abre hacia arriba: hacia abajo se saldría de la página. */
  readonly arriba?: boolean;
}): React.ReactElement {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent): void => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  return (
    <div className={`la-selector${arriba ? " la-selector--arriba" : ""}`} ref={caja}>
      <button
        type="button"
        className="la-selector__boton"
        aria-haspopup="true"
        aria-expanded={abierto}
        aria-label={`${traducir(idioma, "app.idioma")}: ${NOMBRE_DEL_IDIOMA[idioma]}`}
        onClick={() => { setAbierto((a) => !a); }}
      >
        <IconoGlobo tam={18} />
        <span>{idioma.toUpperCase()}</span>
        <IconoFlecha tam={16} />
      </button>
      {abierto ? (
        <ul className="la-selector__lista">
          {IDIOMAS.map((i) => (
            <li key={i}>
              <button
                type="button"
                className="la-selector__opcion"
                aria-current={i === idioma ? "true" : undefined}
                lang={ETIQUETA_BCP47[i]}
                onClick={() => { alCambiar(i); setAbierto(false); }}
              >
                <span>{NOMBRE_DEL_IDIOMA[i]}</span>
                {i === idioma ? <IconoCheck tam={18} /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA CABECERA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tres zonas, y es lo que el dueño dibujó en la captura: la marca a la
 * izquierda, las secciones de la página en el centro, y a la derecha el idioma,
 * «Entrar» y el botón de empezar.
 *
 * En el móvil no caben: las secciones y el idioma pasan a un menú que se abre
 * con el botón de las tres rayas, y en la barra se quedan la marca, «Empezar» y
 * ese botón. Así la barra no se parte en dos filas ni ensancha la página en un
 * móvil de 320, que era el problema que arrastraba la barra de antes.
 *
 * «ENTRAR» SIGUE ANTES QUE «EMPEZAR», Y EN DISCRETO: el cliente que vuelve
 * teclea voicecomfort.com y tiene que encontrar su puerta sin buscar, y el
 * botón grande sigue siendo el de quien todavía no tiene cuenta.
 */
function Cabecera({
  idioma,
  alCambiarDeIdioma,
  seccionActiva,
}: {
  readonly idioma: Idioma;
  readonly alCambiarDeIdioma: (i: Idioma) => void;
  readonly seccionActiva: string | null;
}): React.ReactElement {
  const t = (clave: Clave): string => traducir(idioma, clave);
  const [menu, setMenu] = useState(false);
  const [bajada, setBajada] = useState(false);

  /* La sombra de la barra aparece al bajar: arriba del todo no hay nada debajo
     de lo que separarla. */
  useEffect(() => {
    const mirar = (): void => { setBajada(window.scrollY > 8); };
    mirar();
    window.addEventListener("scroll", mirar, { passive: true });
    return () => { window.removeEventListener("scroll", mirar); };
  }, []);

  useEffect(() => {
    if (!menu) return;
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenu(false);
    };
    document.addEventListener("keydown", tecla);
    return () => { document.removeEventListener("keydown", tecla); };
  }, [menu]);

  return (
    <header className={`la-barra${bajada ? " la-barra--bajada" : ""}${menu ? " la-barra--menu" : ""}`}>
      <div className="la-caja la-barra__fila">
        <a className="la-marca" href="#gancho" onClick={() => { setMenu(false); }}>
          <Isotipo tam={26} />
          <span className="la-marca__palabra">Voice Comfort</span>
        </a>

        <nav className="la-nav" aria-label={t("venta.nav_secciones")}>
          {SECCIONES_DEL_MENU.map((s) => (
            <a
              key={s.id}
              className={`la-nav__enlace${seccionActiva === s.id ? " la-nav__enlace--activo" : ""}`}
              href={`#${s.id}`}
            >
              {t(s.clave)}
            </a>
          ))}
        </nav>

        <div className="la-barra__derecha">
          <div className="la-solo-ancho">
            <SelectorDeIdioma idioma={idioma} alCambiar={alCambiarDeIdioma} />
          </div>
          <a className="la-entrar la-solo-ancho" href={CAMINO_DE_ENTRAR}>{t("cuenta.entrar")}</a>
          <a className="vc-boton la-barra__empezar" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("app.empezar")}</a>
          <button
            type="button"
            className="la-barra__menu"
            aria-expanded={menu}
            aria-controls="la-menu-movil"
            aria-label={menu ? t("venta.menu_cerrar") : t("venta.menu_abrir")}
            onClick={() => { setMenu((m) => !m); }}
          >
            {menu ? <IconoCerrar /> : <IconoMenu />}
          </button>
        </div>
      </div>

      {/* El menú del móvil. Existe siempre en el DOM y se esconde con `hidden`,
          para que `aria-controls` apunte a algo que está. */}
      <div id="la-menu-movil" className="la-menu-movil" hidden={!menu}>
        <nav aria-label={t("venta.nav_secciones")}>
          {SECCIONES_DEL_MENU.map((s) => (
            <a key={s.id} className="la-menu-movil__enlace" href={`#${s.id}`} onClick={() => { setMenu(false); }}>
              {t(s.clave)}
            </a>
          ))}
        </nav>
        <div className="la-menu-movil__idiomas" role="group" aria-label={t("app.idioma")}>
          {IDIOMAS.map((i) => (
            <button
              key={i}
              type="button"
              className={`la-menu-movil__idioma${i === idioma ? " la-menu-movil__idioma--activo" : ""}`}
              aria-current={i === idioma ? "true" : undefined}
              lang={ETIQUETA_BCP47[i]}
              onClick={() => { alCambiarDeIdioma(i); }}
            >
              {NOMBRE_DEL_IDIOMA[i]}
            </button>
          ))}
        </div>
        <a className="la-menu-movil__entrar" href={CAMINO_DE_ENTRAR}>{t("cuenta.entrar")}</a>
      </div>
    </header>
  );
}

/** La cabecera de una sección: etiqueta, titular y entradilla, centrados. */
function CabezaDeSeccion({ b, centrada = true }: { readonly b: Bloque; readonly centrada?: boolean }): React.ReactElement {
  return (
    <div className={`la-cabeza${centrada ? " la-cabeza--centrada" : ""}`} data-aparece="">
      {b.eyebrow ? <p className="vc-etiqueta la-etiqueta">{b.eyebrow}</p> : null}
      <h2>{b.titulo}</h2>
      {b.cuerpo ? <p className="la-entrada">{b.cuerpo}</p> : null}
    </div>
  );
}

/**
 * Los tres sellos que acompañan a cada botón de empezar: lo que quita miedo a
 * pulsar. Desde el 15/09 el primero es la garantía, que es verdad, y no «sin
 * tarjeta ni cuenta», que no lo era.
 */
function Sellos({ idioma, centrados = false }: { readonly idioma: Idioma; readonly centrados?: boolean }): React.ReactElement {
  const t = (clave: Clave): string => traducir(idioma, clave);
  return (
    <ul className={`la-sellos${centrados ? " la-sellos--centrados" : ""}`}>
      <li><IconoEscudo tam={18} />{t("venta.sello_garantia")}</li>
      <li><IconoCheck tam={18} />{t("venta.sello_cancelar")}</li>
      <li><IconoCheck tam={18} />{t("venta.sello_movil")}</li>
    </ul>
  );
}

/* Los iconos de cada lista, en el mismo orden que sus puntos en `copy.ts`. */
const ICONOS_SE_ENTRENA = [IconoLista, IconoReloj, IconoPausa] as const;
const ICONOS_PARA_QUIEN = [
  IconoLibro, IconoAuriculares, IconoMaletin, IconoMicrofono,
  IconoNota, IconoMegafono, IconoCorazon, IconoBocadillo,
] as const;
const ICONOS_ASISTENTE = [IconoReloj, IconoLista, IconoSello] as const;
const ICONOS_DIFERENCIALES = [IconoSello, IconoAltavoz, IconoOnda, IconoTexto, IconoLuna, IconoGrafica] as const;
const ICONOS_SEGURIDAD = [IconoPortapapeles, IconoBrujula, IconoHoja] as const;

function Icono({ lista, i }: { readonly lista: readonly ((p: { tam?: number }) => React.ReactElement)[]; readonly i: number }): React.ReactElement | null {
  const Cual = lista[i];
  return Cual ? <span className="la-icono"><Cual /></span> : null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS SECCIONES, UNA A UNA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cada bloque de `copy.ts` tiene su forma. Un bloque que no esté aquí se pinta
 * con la forma genérica —cabecera y rejilla de puntos— para que un id nuevo en
 * el copy no desaparezca de la página sin avisar.
 */
function Seccion({ b, idioma }: { readonly b: Bloque; readonly idioma: Idioma }): React.ReactElement {
  const t = (clave: Clave): string => traducir(idioma, clave);
  const puntos = b.puntos ?? [];

  switch (b.id) {
    case "gancho":
      return (
        <section id={b.id} className="la-hero">
          <div className="la-caja la-hero__rejilla">
            <div className="la-hero__texto">
              {b.eyebrow ? <p className="vc-etiqueta la-etiqueta">{b.eyebrow}</p> : null}
              <h1>{b.titulo}</h1>
              {b.cuerpo ? <p className="la-hero__cuerpo">{b.cuerpo}</p> : null}
              {/* 15/09: el primario decía «Empezar · sin tarjeta, sin cuenta» y el segundo el
                  precio; el dueño tachó los dos. Queda empezar y ver cómo funciona. */}
              <div className="la-acciones">
                <a className="vc-boton la-boton-grande" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>
                  {t("venta.crear_plan")}
                </a>
                <a className="vc-boton vc-boton--callado la-boton-grande" href="#como-funciona">
                  {t("venta.ver_como_funciona")}
                </a>
              </div>
              <Sellos idioma={idioma} />
            </div>
            {b.foto ? (
              <div className="la-hero__marco">
                {/* La primera cosa que se ve: por eso NO lleva `lazy`. */}
                <img className="la-hero__foto" src={b.foto.src} alt={b.foto.alt} width={1200} height={1500} fetchPriority="high" />
              </div>
            ) : null}
          </div>
        </section>
      );

    case "tu-voz":
      return (
        <section id={b.id} className="la-seccion la-seccion--declaracion">
          <div className="la-caja la-caja--estrecha" data-aparece="">
            {b.eyebrow ? <p className="vc-etiqueta la-etiqueta">{b.eyebrow}</p> : null}
            <h2 className="la-declaracion">{b.titulo}</h2>
            {b.cuerpo ? <p className="la-entrada">{b.cuerpo}</p> : null}
          </div>
        </section>
      );

    case "se-entrena":
    case "seguridad":
      return (
        <section id={b.id} className={`la-seccion${b.id === "seguridad" ? " la-seccion--suave" : ""}`}>
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            <ul className="la-rejilla la-rejilla--tres">
              {puntos.map((p, i) => (
                <li key={p.titulo} className="la-tarjeta la-tarjeta--centrada" data-aparece="">
                  <Icono lista={b.id === "seguridad" ? ICONOS_SEGURIDAD : ICONOS_SE_ENTRENA} i={i} />
                  <h3>{p.titulo}</h3>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      );

    case "para-quien": {
      /* 16/09: las ocho profesiones van en la rejilla y el punto `destacado` («Y para todos los que
         valoran su voz…») sale de ella y va debajo, en un rectángulo de punta a punta. Pedido del
         dueño; antes se pintaban todos los puntos en la rejilla. */
      const profesiones = puntos.filter((p) => p.destacado !== true);
      const paraTodos = puntos.find((p) => p.destacado === true);
      return (
        <section id={b.id} className="la-seccion la-seccion--suave">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            <ul className="la-rejilla la-rejilla--cuatro">
              {profesiones.map((p, i) => (
                <li key={p.titulo} className="la-tarjeta" data-aparece="">
                  <Icono lista={ICONOS_PARA_QUIEN} i={i} />
                  <h3>{p.titulo}</h3>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ul>
            {paraTodos ? (
              <div className="la-para-todos" data-aparece="">
                <h3 className="la-para-todos__titulo">{paraTodos.titulo}</h3>
                <p>{paraTodos.texto}</p>
              </div>
            ) : null}
            <p className="la-accion-centrada">
              <a className="vc-boton la-boton-grande" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("venta.crear_plan")}</a>
            </p>
          </div>
        </section>
      );
    }

    case "como-funciona":
      return (
        <section id={b.id} className="la-seccion">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            <ol className="la-pasos">
              {puntos.map((p, i) => (
                <li key={p.titulo} className="la-paso" data-aparece="">
                  <span className="la-paso__numero vc-cifra" aria-hidden="true">{i + 1}</span>
                  <h3>{p.titulo}</h3>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ol>
            <p className="la-accion-centrada">
              <a className="vc-boton la-boton-grande" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("venta.crear_plan")}</a>
            </p>
          </div>
        </section>
      );

    case "momento":
      return (
        <section id={b.id} className="la-seccion la-seccion--suave">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            {/* En el móvil se desliza de lado —cinco fotos apiladas serían cinco pantallas para
                una sola idea—; en pantalla ancha caben las cinco en fila. `lazy` porque están a
                varias pantallas del principio. */}
            <ul className="la-momentos">
              {puntos.map((p) => (
                <li key={p.titulo} className="la-momento" data-aparece="">
                  {p.foto ? (
                    <img className="la-momento__foto" src={p.foto.src} alt={p.foto.alt} loading="lazy" decoding="async" width={1000} height={1250} />
                  ) : null}
                  <h3>{p.titulo}</h3>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      );

    case "asistente":
      return (
        <section id={b.id} className="la-seccion">
          <div className="la-caja la-dos-columnas">
            <div className="la-dos-columnas__texto">
              <CabezaDeSeccion b={b} centrada={false} />
              <ul className="la-lista-con-iconos">
                {puntos.map((p, i) => (
                  <li key={p.titulo} data-aparece="">
                    <Icono lista={ICONOS_ASISTENTE} i={i} />
                    <div>
                      <h3>{p.titulo}</h3>
                      <p>{p.texto}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {/* Una conversación de muestra, no una pantalla de ejercicio: el dueño pidió no
                enseñar ejercicios («aburren»), y esto enseña lo otro, que siempre hay a quién
                preguntar. */}
            <figure className="la-chat" data-aparece="">
              <figcaption className="la-chat__barra">
                <Isotipo tam={20} />
                <span>{t("venta.chat_nombre")}</span>
              </figcaption>
              <p className="la-chat__burbuja la-chat__burbuja--tu">{t("venta.chat_pregunta")}</p>
              <p className="la-chat__burbuja la-chat__burbuja--asistente">{t("venta.chat_respuesta")}</p>
            </figure>
          </div>
        </section>
      );

    case "diferenciales":
      return (
        <section id={b.id} className="la-seccion">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            <ul className="la-rejilla la-rejilla--tres">
              {puntos.map((p, i) => (
                <li key={p.titulo} className="la-tarjeta" data-aparece="">
                  <Icono lista={ICONOS_DIFERENCIALES} i={i} />
                  <h3>{p.titulo}</h3>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ul>
            <p className="la-accion-centrada">
              <a className="vc-boton la-boton-grande" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("venta.crear_plan")}</a>
            </p>
          </div>
        </section>
      );

    case "precio": {
      const incluye = puntos.filter((p) => p.destacado !== true);
      const garantia = puntos.find((p) => p.destacado === true);
      return (
        <section id={b.id} className="la-seccion la-seccion--suave">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            <div className="la-precio" data-aparece="">
              <p className="la-precio__insignia">{t("precio.lanzamiento")}</p>
              <h3 className="la-precio__plan">{t("venta.precio_plan")}</h3>
              <p className="la-precio__cifra">
                {/*
                  06/10/2026 · El tachado es ahora SOLO un ancla de referencia: con
                  `renuevaAlNormal` en false (lanzamiento fijo de por vida), el normal (119,99) no
                  se cobra nunca. Antes (D27) se cobraba a partir del segundo año. `comprobar-copy.ts`
                  exige que el normal sea mayor que el de lanzamiento para que el tachado no sea un
                  ancla falsa.
                */}
                <s aria-label={t("precio.normal")}>{PRECIO.moneda} {PRECIO.normalUsd}</s>
                <strong>{PRECIO.moneda} {PRECIO.lanzamientoUsd}</strong>
                <span>/ {t("precio.al_ano")}</span>
              </p>
              {/*
                LA MONEDA DEL COBRO, DEBAJO DE LA CIFRA Y NO EN EL PIE (18/09).

                La cifra grande está en dólares y la tarjeta paga pesos, porque la cuenta de la
                pasarela es colombiana y solo liquida COP (el porqué, en `apps/api/src/precio.ts`).
                Eso hay que decirlo ANTES de pedir la tarjeta —FTC, Directiva 2011/83, CDC art. 31,
                los tres que ya obligan con la renovación— y Google marca como engañoso un precio
                anunciado distinto del cobrado.

                Va pegada a la cifra por la misma razón que la renovación: en el pie, junto a los
                enlaces legales, no la lee nadie antes de pulsar. Y la cifra sale de `PRECIO`, no
                escrita a mano, para que no pueda decir una cosa distinta de la que se cobra.
              */}
              <p className="la-precio__moneda">
                {t("precio.nota_moneda").replace("{cop}", elCobroEnPesos(idioma))}
              </p>
              {/* La renovación va PEGADA a la cifra, no en la letra pequeña del pie. Si
                  `renuevaAlNormal` es true, esta línea es obligatoria antes de pedir la tarjeta;
                  06/10/2026 está en false (precio fijo de por vida), así que no se pinta. */}
              {PRECIO.renuevaAlNormal ? (
                <p className="la-precio__renovacion">
                  {t("precio.renovacion").replace("{normal}", `${PRECIO.moneda} ${PRECIO.normalUsd}`)}
                </p>
              ) : null}
              <div className="la-precio__botones">
                <a className="vc-boton la-boton-grande la-boton-ancho" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("venta.crear_plan")}</a>
                <BotonDePagar idioma={idioma} />
              </div>
              <p className="vc-etiqueta la-precio__incluye">{t("venta.precio_incluye")}</p>
              <ul className="la-precio__lista">
                {incluye.map((p) => (
                  <li key={p.titulo}>
                    <span className="la-precio__check"><IconoCheck tam={18} /></span>
                    <div>
                      <strong>{p.titulo}</strong>
                      <span>{p.texto}</span>
                    </div>
                  </li>
                ))}
              </ul>
              {garantia ? (
                <div className="la-garantia">
                  <span className="la-garantia__icono"><IconoEscudo /></span>
                  <div>
                    <p className="la-garantia__titulo">{garantia.titulo}</p>
                    <p>{garantia.texto}</p>
                  </div>
                </div>
              ) : null}
              <p className="la-precio__adultos">{t("venta.solo_adultos")}</p>
            </div>
          </div>
        </section>
      );
    }

    case "preguntas":
      return (
        <section id={b.id} className="la-seccion">
          <div className="la-caja la-caja--media">
            <CabezaDeSeccion b={b} />
            {/* `<details>`: se abre y se cierra sin JavaScript, con teclado, y un lector de
                pantalla dice si está abierta. */}
            <div className="la-faq">
              {puntos.map((p) => (
                <details key={p.titulo} className="la-faq__item">
                  <summary>
                    <span>{p.titulo}</span>
                    <span className="la-faq__signo"><IconoMas tam={20} /></span>
                  </summary>
                  <p>{p.texto}</p>
                </details>
              ))}
            </div>
            <p className="la-faq__mas">
              {t("venta.otra_pregunta")}{" "}
              <a href={ENLACE_DE_WHATSAPP} target="_blank" rel="noopener noreferrer">{t("venta.escribenos")}</a>
            </p>
          </div>
        </section>
      );

    case "cierre":
      return (
        <section id={b.id} className="la-seccion la-cierre">
          <div className="la-caja la-caja--estrecha" data-aparece="">
            {b.eyebrow ? <p className="vc-etiqueta la-etiqueta">{b.eyebrow}</p> : null}
            <h2>{b.titulo}</h2>
            {b.cuerpo ? <p className="la-entrada">{b.cuerpo}</p> : null}
            <p className="la-accion-centrada">
              <a className="vc-boton la-boton-grande" href={CAMINO_DE_EMPEZAR_DESDE_LA_VENTA}>{t("venta.crear_plan")}</a>
            </p>
            <Sellos idioma={idioma} centrados />
          </div>
        </section>
      );

    default:
      return (
        <section id={b.id} className="la-seccion">
          <div className="la-caja">
            <CabezaDeSeccion b={b} />
            {puntos.length > 0 ? (
              <ul className="la-rejilla la-rejilla--tres">
                {puntos.map((p) => (
                  <li key={p.titulo} className="la-tarjeta">
                    <h3>{p.titulo}</h3>
                    <p>{p.texto}</p>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      );
  }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS ENLACES LEGALES DEL PIE (D32)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * SOLO SE ENLAZA LO QUE ESTÁ PUBLICADO, Y ESA ES LA DECISIÓN. Se le pregunta al
 * servidor cuáles hay: pintar los cinco siempre dejaría enlaces a páginas que
 * dicen «esto no está publicado». Y mientras no conteste, no se pinta nada: un
 * hueco que aparece medio segundo después no molesta a nadie; unos enlaces que
 * desaparecen, sí.
 */
function LegalesDelPie({ idioma }: { readonly idioma: Idioma }): React.ReactElement | null {
  const [publicados, setPublicados] = useState<readonly TipoLegal[]>([]);

  useEffect(() => {
    let vivo = true;
    void fetch(`/api/legal?idioma=${idioma}`)
      .then((r) => (r.ok ? (r.json() as Promise<{ publicados?: TipoLegal[] }>) : null))
      .then((d) => { if (vivo) setPublicados(d?.publicados ?? []); })
      .catch(() => { /* Sin enlaces se lee igual. El contacto de al lado sigue. */ });
    return () => { vivo = false; };
  }, [idioma]);

  if (publicados.length === 0) return null;

  return (
    <nav className="la-pie__columna" aria-label={traducir(idioma, "pie.legales")}>
      <p className="la-pie__titulo">{traducir(idioma, "pie.legales")}</p>
      <ul>
        {publicados.map((tipo) => (
          <li key={tipo}>
            <a href={`${caminoDelLegal(tipo)}?idioma=${idioma}`}>{NOMBRE_DEL_LEGAL[idioma][tipo]}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL PIE (15/09/2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «Mejora mucho el footer», y de la dirección postal: «quita eso». Era una
 * lista de definiciones en una columna estrecha, con la dirección entre el
 * correo y el horario.
 *
 * Ahora son cuatro columnas —la marca con su lema y el idioma, las secciones,
 * la ayuda (WhatsApp, correo y horario) y los documentos legales— y debajo, en
 * una franja, el aviso legal, que es lo que sostiene todo lo de arriba. En el
 * móvil las columnas se apilan.
 *
 * El contacto sale de `contacto.ts` por su nombre de constante
 * (`verificar:contacto`), nunca escrito a mano.
 */
function ElPie({
  idioma,
  pie,
  alCambiarDeIdioma,
}: {
  readonly idioma: Idioma;
  readonly pie: string;
  readonly alCambiarDeIdioma: (i: Idioma) => void;
}): React.ReactElement {
  const t = (clave: Clave): string => traducir(idioma, clave);
  const entreSemana = t("contacto.entre_semana")
    .replace("{a}", laHora(HORARIO.entreSemana.abre, idioma))
    .replace("{b}", laHora(HORARIO.entreSemana.cierra, idioma));
  const sabado = t("contacto.sabado")
    .replace("{a}", laHora(HORARIO.sabado.abre, idioma))
    .replace("{b}", laHora(HORARIO.sabado.cierra, idioma));

  return (
    <footer className="la-pie">
      <div className="la-caja">
        <div className="la-pie__rejilla">
          <div className="la-pie__columna la-pie__columna--marca">
            <a className="la-marca" href="#gancho">
              <Isotipo tam={28} />
              <span className="la-marca__palabra">Voice Comfort</span>
            </a>
            <p className="la-pie__lema">{t("venta.pie_lema")}</p>
            <SelectorDeIdioma idioma={idioma} alCambiar={alCambiarDeIdioma} arriba />
          </div>

          <nav className="la-pie__columna" aria-label={t("venta.nav_secciones")}>
            <p className="la-pie__titulo">Voice Comfort</p>
            <ul>
              {SECCIONES_DEL_MENU.map((s) => (
                <li key={s.id}><a href={`#${s.id}`}>{t(s.clave)}</a></li>
              ))}
            </ul>
          </nav>

          <div className="la-pie__columna">
            <p className="la-pie__titulo">{t("venta.pie_ayuda")}</p>
            <ul>
              <li>
                <a href={ENLACE_DE_WHATSAPP} target="_blank" rel="noopener noreferrer">
                  {t("contacto.whatsapp")} · {TELEFONO_LEIDO}
                </a>
              </li>
              <li><a href={ENLACE_DE_CORREO}>{CORREO_DE_CONTACTO}</a></li>
              <li className="la-pie__horario">
                <span>{entreSemana}.</span>
                <span>{sabado}.</span>
                {/* La zona va dicha: «de 8 a 18» sin zona es una hora que cada quien lee en la
                    suya, y este producto se vende en tres mercados. */}
                <span className="la-pie__zona">{t("contacto.zona").replace("{zona}", HORARIO.desplazamiento)}</span>
              </li>
            </ul>
          </div>

          <LegalesDelPie idioma={idioma} />
        </div>

        <div className="la-pie__base">
          <p>© {new Date().getFullYear()} Voice Comfort. {t("venta.pie_derechos")}</p>
          <p className="la-pie__legal">{pie}</p>
        </div>
      </div>
    </footer>
  );
}

export function App(): React.ReactElement {
  /**
   * El idioma sale de lo que pide el navegador, no de una bandera que haya que
   * buscar. Y se puede cambiar: quien lee en dos idiomas suele preferir uno
   * concreto para leer de un producto de salud.
   */
  const [idioma, setIdioma] = useState<Idioma>(() => {
    /*
      Y LA URL MANDA SOBRE LO GUARDADO, que es lo que hace que compartir sirva
      de algo: quien recibe `?idioma=pt` tiene que ver portugués aunque él
      eligiera inglés aquí la semana pasada.
    */
    const deLaUrl = leerIdiomaDeLaUrl();
    if (deLaUrl) return deLaUrl;
    const guardado = leerIdiomaGuardado();
    if (guardado) return guardado;
    /*
      El `IDIOMA_POR_DEFECTO` de aquí no es decoración: la verdad del idioma
      por defecto vive en un solo sitio, `idiomas.ts`, y aquí se lee.
    */
    return typeof navigator === "undefined"
      ? IDIOMA_POR_DEFECTO
      : negociarIdioma(navigator.languages?.join(","));
  });

  function cambiarDeIdioma(i: Idioma): void {
    setIdioma(i);
    guardarIdioma(i);
    ponerIdiomaEnLaUrl(i);
  }

  /*
    AVISAR DE QUE ALGUIEN HA LLEGADO · una vez, y sin guardarle nada (E4).

    Un `POST` sin cuerpo, que contesta 204. No se le guarda nada a quien llega
    —ni galleta, ni `sessionStorage`— y por eso no hay banner de consentimiento.
    El array de dependencias va vacío: cambiar de idioma no es llegar otra vez.
  */
  useEffect(() => {
    void fetch("/api/visita", { method: "POST" }).catch(() => {
      /* Contar es lo de menos: si esto falla, la página se lee igual. */
    });
  }, []);

  // El atributo `lang` no es decorativo: es lo que hace que un lector de
  // pantalla pronuncie la página en el idioma correcto. Y el título de la
  // pestaña y la descripción, que se quedaban en el idioma con el que llegó.
  useEffect(() => {
    document.documentElement.lang = ETIQUETA_BCP47[idioma];
    document.title = traducir(idioma, "cabecera.titulo");
    const descripcion = document.querySelector('meta[name="description"]');
    if (descripcion) descripcion.setAttribute("content", traducir(idioma, "cabecera.descripcion"));
  }, [idioma]);

  /*
    LOS DATOS ESTRUCTURADOS, y se pintan aquí y no en el `index.html`: llevan
    una cifra —el precio— y una cifra escrita a mano en la cabecera saldría de
    la red de `verificar:cifras`.

    Y LA DISPONIBILIDAD NO SE SUPONE (10/09): lo sabe el servidor, que en
    `/api/precio` trae `sePuedeComprar`. Mientras no conteste, o si contesta que
    no, la oferta va SIN disponibilidad —el precio sigue siendo verdad—.
  */
  const [sePuedeComprar, setSePuedeComprar] = useState(false);
  useEffect(() => {
    let vivo = true;
    fetch("/api/precio")
      .then((r) => (r.ok ? (r.json() as Promise<{ sePuedeComprar?: unknown }>) : null))
      .then((d) => { if (vivo) setSePuedeComprar(d?.sePuedeComprar === true); })
      .catch(() => { /* sin respuesta, se queda sin disponibilidad: no se afirma nada */ });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    const ID = "vc-datos-estructurados";
    document.getElementById(ID)?.remove();

    const datos = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Voice Comfort",
      description: COPY[idioma].bloques[0]?.cuerpo ?? "",
      brand: { "@type": "Brand", name: "Voice Comfort" },
      url: "https://voicecomfort.com/",
      image: "https://voicecomfort.com/activos/landing/tarjeta.png",
      offers: {
        "@type": "Offer",
        price: PRECIO.lanzamientoUsd,
        priceCurrency: "USD",
        url: "https://voicecomfort.com/",
        ...(sePuedeComprar ? { availability: "https://schema.org/InStock" } : {}),
      },
    };

    const e = document.createElement("script");
    e.id = ID;
    e.type = "application/ld+json";
    e.textContent = JSON.stringify(datos);
    document.head.appendChild(e);
  }, [idioma, sePuedeComprar]);

  /*
    15/09 · LO QUE APARECE AL BAJAR, Y LA SECCIÓN EN LA QUE SE ESTÁ.

    «A cada scroll, profundizar en esa historia»: cada pieza marcada con
    `data-aparece` entra suave cuando llega a la pantalla. Quien pide menos
    movimiento, o un navegador sin `IntersectionObserver`, la ve puesta desde
    el principio — lo que se anima es la entrada, nunca el contenido.

    Y el menú de la cabecera marca en qué sección está quien lee.
  */
  const [seccionActiva, setSeccionActiva] = useState<string | null>(null);
  useEffect(() => {
    const piezas = Array.from(document.querySelectorAll<HTMLElement>("[data-aparece]"));
    const sinMovimiento =
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (sinMovimiento) {
      for (const p of piezas) p.classList.add("la-visible");
    }
    const aparecer = sinMovimiento
      ? null
      : new IntersectionObserver(
          (entradas) => {
            for (const e of entradas) {
              if (!e.isIntersecting) continue;
              e.target.classList.add("la-visible");
              aparecer?.unobserve(e.target);
            }
          },
          { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
        );
    if (aparecer) for (const p of piezas) aparecer.observe(p);

    const ids = SECCIONES_DEL_MENU.map((s) => s.id);
    const vigia =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(
            (entradas) => {
              for (const e of entradas) {
                if (e.isIntersecting) setSeccionActiva(e.target.id);
              }
            },
            { rootMargin: "-45% 0px -50% 0px" },
          );
    if (vigia) {
      for (const id of ids) {
        const s = document.getElementById(id);
        if (s) vigia.observe(s);
      }
    }
    return () => {
      aparecer?.disconnect();
      vigia?.disconnect();
    };
  }, [idioma]);

  const { bloques, pie } = COPY[idioma];

  return (
    <div className="la">
      <Cabecera idioma={idioma} alCambiarDeIdioma={cambiarDeIdioma} seccionActiva={seccionActiva} />
      <main>
        {bloques.map((b) => (
          <Seccion key={b.id} b={b} idioma={idioma} />
        ))}
      </main>
      <ElPie idioma={idioma} pie={pie} alCambiarDeIdioma={cambiarDeIdioma} />
      <WhatsAppFlotante idioma={idioma} />
    </div>
  );
}
